import { useState, useEffect, useMemo } from 'react';
import { PaymentMethodsService } from '../services/PaymentMethodsService';
import { PaymentMethodOption } from '../adapters/PaymentMethodsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';

// Cache global para evitar llamadas duplicadas
let paymentMethodsCache: PaymentMethodOption[] | null = null;
let isLoading = false;
let loadPromise: Promise<PaymentMethodOption[]> | null = null;

export const usePaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodOption[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Crear instancia única del servicio
  const service = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new PaymentMethodsService(httpClient);
  }, []);

  const loadPaymentMethods = async () => {
    // Si ya tenemos datos en cache, usarlos
    if (paymentMethodsCache) {
      setPaymentMethods(paymentMethodsCache);
      return;
    }

    // Si ya hay una carga en progreso, esperar a que termine
    if (isLoading && loadPromise) {
      try {
        const methods = await loadPromise;
        setPaymentMethods(methods);
      } catch (err: any) {
        setError(err.message || 'Error al cargar medios de pago');
      }
      return;
    }

    // Iniciar nueva carga
    try {
      setLoading(true);
      setError(null);
      isLoading = true;

      // Crear la promesa de carga
      loadPromise = service.getPaymentMethods();
      const methods = await loadPromise;

      // Guardar en cache
      paymentMethodsCache = methods;
      setPaymentMethods(methods);
    } catch (err: any) {
      setError(err.message || 'Error al cargar medios de pago');
    } finally {
      setLoading(false);
      isLoading = false;
      loadPromise = null;
    }
  };

  useEffect(() => {
    loadPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Carga inicial

  return {
    paymentMethods,
    loading,
    error,
    refetch: () => {
      // Limpiar cache para forzar recarga
      paymentMethodsCache = null;
      isLoading = false;
      loadPromise = null;
      loadPaymentMethods();
    },
  };
};
